import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { profileAPI, friendsAPI, paymentsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import Layout from '../../components/Layout';
import { motion } from 'framer-motion';
import { Camera, User, Save, Loader2, Award, Users, ChevronRight, Flame, X, Trophy, Star, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POSITIONS = ['Forward', 'Defense', 'Goalie', 'Center', 'Wing'];
const HANDS = ['Left', 'Right'];
const GENDER_OPTIONS = [
  { label: 'Boy', value: 'boy' },
  { label: 'Girl', value: 'girl' },
];

export default function MyProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [friends, setFriends] = useState([]);
  const [friendsBoard, setFriendsBoard] = useState([]);
  const [form, setForm] = useState({ name: '', position: '', shooting_hand: '', current_team: '', gender: '', phone: '', sms_opt_in: false, referral_source: '', referral_detail: '' });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [profRes, friendsRes, boardRes] = await Promise.all([
        profileAPI.me(), friendsAPI.list(), friendsAPI.leaderboard()
      ]);
      const u = profRes.data;
      setProfile(u);
      const p = u.profile || {};
      setForm({
        name: u.name || '',
        position: p.position || '',
        shooting_hand: p.shooting_hand || '',
        current_team: p.current_team || '',
        gender: p.gender || '',
        phone: p.phone || '',
        sms_opt_in: !!p.sms_opt_in,
        referral_source: p.referral_source || '',
        referral_detail: p.referral_detail || '',
      });
      setFriends(friendsRes.data || []);
      setFriendsBoard(boardRes.data || []);
    } catch {}
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await profileAPI.update(form);
      toast.success('Profile saved');
      await load();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleHeadshot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await profileAPI.uploadHeadshot(file);
      toast.success('Photo uploaded!');
      await load();
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  if (loading) return <Layout title="PROFILE" showBack><div className="flex justify-center py-24"><div className="spinner" /></div></Layout>;

  const badges = profile?.badges || [];
  const headshot = profile?.headshot_url ? `${API_URL}${profile.headshot_url}` : null;

  return (
    <Layout title="MY PROFILE" showBack>
      <div className="px-4 py-6 space-y-5">
        {/* Avatar + Name */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center gap-3">
          <div className="relative" data-testid="headshot-area">
            <div className="w-24 h-24 rounded-2xl bg-white border-2 border-[#E2E8F0] overflow-hidden flex items-center justify-center">
              {headshot ? (
                <img src={headshot} alt="Headshot" className="w-full h-full object-cover" data-testid="headshot-img" />
              ) : (
                <User size={40} className="text-[#1C2B54]" />
              )}
            </div>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="absolute -bottom-2 -right-2 w-8 h-8 bg-[#2563EB] rounded-full flex items-center justify-center border-2 border-white"
              data-testid="upload-headshot-btn"
            >
              {uploading ? <Loader2 size={14} className="animate-spin text-white" /> : <Camera size={14} className="text-white" />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleHeadshot} className="hidden" />
          </div>
          <div className="text-center">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold" data-testid="profile-name">{profile?.name}</h2>
            <p className="text-[10px] text-[#64748B] uppercase tracking-widest">{profile?.email}</p>
          </div>
        </motion.div>

        {/* Badges */}
        {badges.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="bg-white border border-[#E2E8F0] rounded-xl p-4"
          >
            <p className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold mb-2">Badges</p>
            <div className="flex gap-3" data-testid="profile-badges">
              {badges.map(b => (
                <div key={b.name} className="flex flex-col items-center gap-1">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center border-2" style={{ borderColor: b.color, background: `${b.color}15` }}>
                    <Award size={20} style={{ color: b.color }} />
                  </div>
                  <span className="text-[9px] font-bold" style={{ color: b.color }}>{b.name}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Top Friends Leaderboard */}
        {friendsBoard.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.07 }}
            className="bg-white border border-[#E2E8F0] rounded-xl p-4"
          >
            <p className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold mb-3 flex items-center gap-1">
              <Trophy size={12} className="text-[#FF9100]" /> Top Friends This Week
            </p>
            <div className="space-y-1.5" data-testid="friends-leaderboard">
              {friendsBoard.slice(0, 5).map((f, i) => (
                <div
                  key={f.id}
                  onClick={() => navigate(`/profile/${f.id}`)}
                  className={`flex items-center gap-2.5 p-2.5 rounded-lg cursor-pointer transition-all active:scale-[0.98] ${
                    f.is_you
                      ? 'bg-[#2563EB]/10 border border-[#2563EB]/25'
                      : 'bg-[#F8FAFC] hover:bg-[#F8FAFC]/70'
                  }`}
                  data-testid={`friend-rank-${i}`}
                >
                  <span className={`text-xs font-black w-5 text-center ${i === 0 ? 'text-yellow-400' : i === 1 ? 'text-gray-300' : i === 2 ? 'text-amber-600' : 'text-[#64748B]'}`}>
                    {f.rank}
                  </span>
                  <div className="w-7 h-7 rounded-full bg-[#E2E8F0] overflow-hidden flex items-center justify-center flex-shrink-0">
                    {f.headshot_url ? (
                      <img src={`${API_URL}${f.headshot_url}`} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User size={12} className="text-[#64748B]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold truncate">{f.name}</span>
                      {f.is_you && <span className="text-[7px] bg-[#2563EB] text-white px-1 py-0.5 rounded-full font-bold">YOU</span>}
                    </div>
                    {f.position && <p className="text-[9px] text-[#00E5FF] font-bold uppercase">{f.position}</p>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {f.streak > 0 && (
                      <div className="flex items-center gap-0.5">
                        <Flame size={10} className="text-[#FF9100]" />
                        <span className="text-[10px] font-bold text-[#FF9100]">{f.streak}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-0.5">
                      <Star size={10} className="text-[#2563EB]" />
                      <span className="font-['Barlow_Condensed'] text-base font-bold text-[#2563EB]">{f.weekly_score}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Edit Fields */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-4"
        >
          <p className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold">Edit Profile</p>

          {/* Name */}
          <div>
            <label className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold block mb-1">Name</label>
            <input
              value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full p-2.5 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-sm text-[#0A2242] focus:border-[#2563EB] focus:outline-none"
              data-testid="edit-name"
            />
          </div>

          {/* Position */}
          <div>
            <label className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold block mb-1">Position</label>
            <div className="flex flex-wrap gap-2">
              {POSITIONS.map(pos => (
                <button
                  key={pos}
                  onClick={() => setForm(f => ({ ...f, position: pos }))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    form.position === pos
                      ? 'bg-[#2563EB] text-white'
                      : 'bg-[#F8FAFC] border border-[#E2E8F0] text-[#64748B]'
                  }`}
                  data-testid={`pos-${pos.toLowerCase()}`}
                >{pos}</button>
              ))}
            </div>
          </div>

          {/* Shooting Hand */}
          <div>
            <label className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold block mb-1">Shooting Hand</label>
            <div className="flex gap-2">
              {HANDS.map(h => (
                <button
                  key={h}
                  onClick={() => setForm(f => ({ ...f, shooting_hand: h }))}
                  className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-all ${
                    form.shooting_hand === h
                      ? 'bg-[#2563EB] text-white'
                      : 'bg-[#F8FAFC] border border-[#E2E8F0] text-[#64748B]'
                  }`}
                  data-testid={`hand-${h.toLowerCase()}`}
                >{h}</button>
              ))}
            </div>
          </div>

          {/* Gender */}
          <div>
            <label className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold block mb-1">Gender</label>
            <div className="flex gap-2">
              {GENDER_OPTIONS.map(g => (
                <button
                  key={g.value}
                  onClick={() => setForm(f => ({ ...f, gender: g.value }))}
                  className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-all ${
                    form.gender === g.value
                      ? 'bg-[#2563EB] text-white'
                      : 'bg-[#F8FAFC] border border-[#E2E8F0] text-[#64748B]'
                  }`}
                  data-testid={`profile-gender-${g.value}`}
                >{g.label}</button>
              ))}
            </div>
          </div>

          {/* Team */}
          <div>
            <label className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold block mb-1">Current Team</label>
            <input
              value={form.current_team} onChange={e => setForm(f => ({ ...f, current_team: e.target.value }))}
              placeholder="e.g. Central Catholic"
              className="w-full p-2.5 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-sm text-[#0A2242] placeholder:text-[#64748B] focus:border-[#2563EB] focus:outline-none"
              data-testid="edit-team"
            />
          </div>

          {/* Cell Phone + SMS opt-in */}
          <div>
            <label className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold block mb-1">Cell Phone</label>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              placeholder="(555) 123-4567"
              className="w-full p-2.5 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-sm text-[#0A2242] placeholder:text-[#64748B] focus:border-[#2563EB] focus:outline-none"
              style={{ fontSize: '16px' }}
              data-testid="edit-phone"
            />
            {form.phone && (
              <label className="flex items-start gap-2 mt-2 px-1 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.sms_opt_in}
                  onChange={e => setForm(f => ({ ...f, sms_opt_in: e.target.checked }))}
                  className="mt-0.5 accent-[#2563EB] w-4 h-4 cursor-pointer"
                  data-testid="edit-sms-optin"
                />
                <span className="text-[10px] text-[#64748B] leading-snug">
                  Yes, send me coach alerts &amp; updates by text. Reply STOP to unsubscribe.
                </span>
              </label>
            )}
          </div>

          {/* How heard about us */}
          <div>
            <label className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold block mb-1">How did you hear about us?</label>
            <select
              value={form.referral_source}
              onChange={e => setForm(f => ({ ...f, referral_source: e.target.value }))}
              className="w-full p-2.5 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-sm text-[#0A2242] focus:border-[#2563EB] focus:outline-none"
              data-testid="edit-referral-source"
            >
              <option value="">— Select —</option>
              <option value="Coach / Teammate">Coach / Teammate</option>
              <option value="Instagram">Instagram</option>
              <option value="Facebook">Facebook</option>
              <option value="TikTok">TikTok</option>
              <option value="Friend / Word of Mouth">Friend / Word of Mouth</option>
              <option value="Pete Lenes directly">Pete Lenes directly</option>
              <option value="Google / Search">Google / Search</option>
              <option value="Elev802 Camp / Clinic">Elev802 Camp / Clinic</option>
              <option value="Other">Other</option>
            </select>
            {form.referral_source && (
              <input
                type="text"
                value={form.referral_detail}
                onChange={e => setForm(f => ({ ...f, referral_detail: e.target.value }))}
                placeholder="Optional detail (who, where, etc.)"
                className="w-full mt-2 p-2.5 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] text-sm text-[#0A2242] placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:outline-none"
                style={{ fontSize: '16px' }}
                maxLength={200}
                data-testid="edit-referral-detail"
              />
            )}
          </div>

          <button
            onClick={handleSave} disabled={saving}
            className="w-full bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold uppercase tracking-wide rounded-lg py-3 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50"
            data-testid="save-profile-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </motion.div>

        {/* Friends */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold flex items-center gap-1">
              <Users size={12} /> Friends ({friends.length})
            </p>
          </div>
          {friends.length === 0 ? (
            <p className="text-xs text-[#64748B] text-center py-3">Add friends from the Community Leaderboard!</p>
          ) : (
            <div className="space-y-2">
              {friends.map(f => (
                <div key={f.id} className="flex items-center gap-3 p-2 rounded-lg bg-[#F8FAFC]">
                  <div className="w-8 h-8 rounded-full bg-[#E2E8F0] overflow-hidden flex items-center justify-center flex-shrink-0">
                    {f.headshot_url ? (
                      <img src={`${API_URL}${f.headshot_url}`} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User size={14} className="text-[#64748B]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold truncate">{f.name}</p>
                    <p className="text-[9px] text-[#64748B]">{f.position} {f.current_team ? `· ${f.current_team}` : ''}</p>
                  </div>
                  {f.badges?.length > 0 && (
                    <div className="flex gap-0.5">
                      {f.badges.map(b => (
                        <Award key={b.name} size={12} style={{ color: b.color }} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Manage Subscription */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-4"
        >
          <h3 className="font-['Barlow_Condensed'] text-xs font-bold uppercase tracking-widest text-[#64748B] mb-3">Subscription</h3>
          <button
            onClick={async () => {
              const toastId = toast.loading('Opening Stripe portal...');
              try {
                const res = await paymentsAPI.portal(window.location.origin + '/profile');
                if (res.data?.url) {
                  toast.dismiss(toastId);
                  window.location.href = res.data.url;
                } else {
                  toast.error('Could not open portal', { id: toastId });
                }
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Could not open portal', { id: toastId });
              }
            }}
            className="w-full flex items-center justify-between bg-[#F8FAFC] hover:bg-[#F1F5F9] border border-[#E2E8F0] rounded-lg px-4 py-3 transition-colors"
            data-testid="manage-subscription-btn"
          >
            <div className="flex items-center gap-2.5">
              <CreditCard size={16} className="text-[#635BFF]" />
              <div className="text-left">
                <p className="text-sm font-bold text-[#0A2242]">Manage Subscription</p>
                <p className="text-[10px] text-[#64748B]">Cancel · Update card · View invoices</p>
              </div>
            </div>
            <ChevronRight size={16} className="text-[#94A3B8]" />
          </button>
        </motion.div>
      </div>
    </Layout>
  );
}
